import type { NextPage } from 'next'
import { paths } from '@/constants/paths'
import { AboutContextProvider } from '@/contexts/AboutContext'
import { Seo } from '@/layout/Seo'
import { GUILD_MEMBERSHIP_NUMBERS_ID, OUR_IMPACT_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { GET_PROJECT_TILES } from '@/services/ProjectsService'
import { Collection } from '@/types/codegen-contentful'
import { GetProjectTilesQuery } from '@/types/codegen-federation'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { AboutView } from '@/views/AboutView'

interface AboutProps {
  impact: Collection
  locale: string
  preview: boolean
  tiles: GetProjectTilesQuery
  members?: string
  lastUpdated?: string
  guildMembershipNumbers: Collection
}

const Home: NextPage<AboutProps> = ({ impact, locale, tiles, members, lastUpdated, guildMembershipNumbers }) => {
  const { t } = useTranslate('about')
  const url = `${getBasePath(locale)}`
  const title = t('angelStudiosAboutTitle', 'About Angel Studios | Angel Studios')
  const description = t(
    'angelStudiosAboutDescription',
    'Angel Studios is a leading crowd-funded streaming service and film studio whose mission is to create stories that amplify the light. Learn more about us here.',
  )

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.about.index}
        title={title}
      />
      <AboutContextProvider
        impact={impact}
        tiles={tiles}
        numberOfGuildMembers={members}
        lastUpdated={lastUpdated}
        guildMembershipNumbers={guildMembershipNumbers}
      >
        <AboutView />
      </AboutContextProvider>
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/about/index.tsx' },
  async ({ federationClient, locale, preview }) => {
    const impact = await getCollection({ locale, preview }, OUR_IMPACT_ENTRY_ID)
    const guildMembershipNumbers = await getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID)

    const { data: tiles } = await federationClient.query<GetProjectTilesQuery>({
      query: GET_PROJECT_TILES,
      errorPolicy: 'all',
    })

    const members = guildMembershipNumbers?.customData[0]?.numSubscribers ?? null
    const lastUpdated = guildMembershipNumbers?.customData[0]?.updatedAt ?? null

    return {
      props: {
        impact,
        locale,
        preview,
        tiles,
        members,
        lastUpdated,
        guildMembershipNumbers,
        ...(await loadTranslations(locale, ['common', 'about', 'app-promo', 'account'])),
      },
    }
  },
)

export default Home
